/**
 * The edit-team dialog (platform design §5.2.1; Figma `638:18821`, card 4): changes a team's
 * name, lead and parent. The level itself is fixed -- it is what holds the teams below this one
 * in place -- so it renders as a chip, never a control, and `Dentro de` offers only the level
 * directly above rather than every level `ParentPicker` shows a new team (`levelPosition`
 * restriction on the same component, via `excludeTop`). `Salvar` sends only the fields the
 * reader actually changed, diffed against `team` for name and parent and tracked separately for
 * the lead, whose picker cannot always reconstruct the original selection from `team` alone (an
 * account lead needs the full tracked-account row, not just its id).
 */
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';

import type { ApiFailure } from '../../../lib/http/errors';
import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { FormDialog } from '../../../chrome/form';
import { fieldErrorOf } from '../../../chrome/form/fieldErrorOf';
import { Chip } from '../../../ui/chip';
import { Field, FieldError, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { useCan } from '../../session/orgContext';
import { t, plural } from '../i18n';
import type { TrackedAccountRow } from '../accounts/api/schemas';
import { useTrackedAccounts } from '../accounts/api/queries';
import { LeadPicker, type LeadSelection } from './LeadPicker';
import { leadFields } from './NewTeam';
import { ParentPicker, type ParentSelection } from './ParentPicker';
import { useStructure, useTeamUpdate } from './hooks';
import type { TeamOut, TeamUpdateIn } from './schemas';

/** A failure naming a field renders on that field; every other failure renders as one fixed
 *  sentence -- the taxonomy's own wording ("server error", "offline") never reaches a reader
 *  trying to save a team. */
function dialogFailure(failure: ApiFailure | null): ApiFailure | null {
  if (failure === null) return null;
  if (failure.kind === 'invalid' && failure.field !== null) return failure;
  return { kind: 'invalid', field: null, message: t('teams.editTeam.failed') };
}

function leadSelectionOf(team: TeamOut, accounts: TrackedAccountRow[]): LeadSelection | null {
  if (team.lead_account_id !== null) {
    const account = accounts.find((candidate) => candidate.id === team.lead_account_id);
    if (account !== undefined) return { kind: 'account', account };
  }
  if (team.lead_name !== null) return { kind: 'name', name: team.lead_name };
  return null;
}

/** The team plus everything under it that moves with a parent change, itself included --
 *  the same "subtree" `subtree_accounts` already counts. */
function subtreeTeamCount(teamId: string, teams: TeamOut[]): number {
  const childrenOf = new Map<string, string[]>();
  for (const team of teams) {
    if (team.parent_team_id === null) continue;
    const siblings = childrenOf.get(team.parent_team_id) ?? [];
    siblings.push(team.id);
    childrenOf.set(team.parent_team_id, siblings);
  }
  let count = 0;
  const stack = [teamId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    count += 1;
    stack.push(...(childrenOf.get(id) ?? []));
  }
  return count;
}

export interface EditTeamProps {
  campaignId: string;
  team: TeamOut;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditTeam(props: EditTeamProps): ReactNode {
  const { campaignId, team, open, onOpenChange } = props;
  const canWrite = useCan('control_plane.write');
  const structureQuery = useStructure(campaignId);
  const accountsQuery = useTrackedAccounts(campaignId);
  const updateMutation = useTeamUpdate(campaignId);

  const [name, setName] = useState(team.name);
  const [lead, setLeadValue] = useState<LeadSelection | null>(null);
  const [leadTouched, setLeadTouched] = useState(false);
  const [parent, setParent] = useState<ParentSelection>({
    parentTeamId: team.parent_team_id,
    levelId: team.level_id,
  });
  const [nameError, setNameError] = useState<string | null>(null);
  const nameId = useId();

  function setLead(value: LeadSelection | null): void {
    setLeadValue(value);
    setLeadTouched(true);
  }

  // Re-derived every time the dialog opens, never carried across a previous open of a
  // different team (`TeamActions` reuses one `EditTeam` element position per row, but never
  // the same one across two teams at once).
  useEffect(() => {
    if (!open) return;
    setName(team.name);
    setLeadValue(leadSelectionOf(team, accountsQuery.data ?? []));
    setLeadTouched(false);
    setParent({ parentTeamId: team.parent_team_id, levelId: team.level_id });
    setNameError(null);
    // `accountsQuery.data` is read once per open, not tracked afterwards -- re-deriving the
    // lead chip every time the list refetches would fight a reader mid-edit.
  }, [open, team]);

  if (!canWrite) return null;

  const levels = structureQuery.data?.levels ?? [];
  const teams = structureQuery.data?.teams ?? [];
  const aboveLevel = levels.find((level) => level.position === team.level_position - 1);
  const ownLevel = levels.find((level) => level.id === team.level_id);

  const failure = updateMutation.error?.failure ?? null;
  const nameServerError = fieldErrorOf(failure, 'name');

  function handleSubmit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    const trimmedName = name.trim();
    setNameError(trimmedName === '' ? t('teams.newTeam.name.required') : null);
    if (trimmedName === '') return;

    const body: TeamUpdateIn = {};
    if (trimmedName !== team.name) body.name = trimmedName;
    if (parent.parentTeamId !== team.parent_team_id) body.parent_team_id = parent.parentTeamId;
    if (leadTouched) {
      const { lead_name, lead_account_id } = leadFields(lead);
      body.lead_name = lead_name;
      body.lead_account_id = lead_account_id;
    }

    return updateMutation.mutateAsync({ teamId: team.id, body });
  }

  const destinationTeam = teams.find((candidate) => candidate.id === parent.parentTeamId);
  const parentChanged = parent.parentTeamId !== team.parent_team_id;
  const locale = getLocale();

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('teams.editTeam.title')}
      submitLabel={t('teams.editTeam.submit')}
      pending={updateMutation.isPending}
      failure={dialogFailure(failure)}
      onSubmit={handleSubmit}
    >
      {/* `FormDialog`'s title is a plain string, so the fixed level -- card 4 draws it beside
       *  the dialog's own title -- renders as the body's first line instead of inside the
       *  header this component does not own. */}
      <div className="-mt-2 flex items-center gap-2">
        <span className="text-caption text-muted-foreground">
          {t('teams.editTeam.level.label')}
        </span>
        <Chip size="sm">{team.level_name}</Chip>
      </div>

      {aboveLevel !== undefined && ownLevel !== undefined && (
        <Field>
          <FieldLabel>{t('teams.newTeam.parent.label')}</FieldLabel>
          <ParentPicker
            levels={[aboveLevel, ownLevel]}
            teams={teams}
            value={parent}
            onChange={setParent}
            excludeTop
          />
          {parentChanged && destinationTeam !== undefined && (
            <div className="flex flex-wrap items-center gap-1.5 text-caption text-muted-foreground">
              {t('teams.editTeam.moveSubtree.label', { name: destinationTeam.name })}
              <Chip size="sm">
                {plural(
                  'teams.editTeam.moveSubtree.teams.other',
                  subtreeTeamCount(team.id, teams),
                  { count: integer(subtreeTeamCount(team.id, teams), locale) },
                )}
              </Chip>
              <Chip size="sm">
                {plural('teams.accounts.other', team.subtree_accounts, {
                  count: integer(team.subtree_accounts, locale),
                })}
              </Chip>
            </div>
          )}
        </Field>
      )}

      <Field data-invalid={nameError !== null || undefined}>
        <FieldLabel htmlFor={nameId}>{t('teams.newTeam.name.label')}</FieldLabel>
        <Input id={nameId} value={name} onChange={(event) => setName(event.target.value)} />
        <FieldError>{nameError ?? nameServerError}</FieldError>
      </Field>

      <Field>
        <FieldLabel>{t('teams.newTeam.lead.label')}</FieldLabel>
        <LeadPicker campaignId={campaignId} value={lead} onChange={setLead} />
      </Field>
    </FormDialog>
  );
}
